<i18n src="./resources/locales.json"></i18n>

<template>
  <v-container>
    <h1 class="ml-3 primary--text">{{ $t('preOrdersTitle') }}</h1>
    <h2 class="ml-3 primary--text text-body-1 mb-4">{{ $t('preOrdersSubtitle') }}</h2>

    <v-row class="ml-3 mr-3 mb-2 align-center" no-gutters style="flex-wrap: wrap; gap: 8px;">
      <v-text-field
        v-model="searchClient"
        :label="$t('searchClient')"
        prepend-inner-icon="mdi-magnify"
        background-color="primaryVariant2"
        outlined
        dense
        hide-details
        flat
        clearable
        class="pre-order-filter"
        style="max-width: 240px;"
      />
      <v-text-field
        v-model="searchEngineSeries"
        :label="$t('searchEngineSeries')"
        prepend-inner-icon="mdi-magnify"
        background-color="primaryVariant2"
        outlined
        dense
        hide-details
        flat
        clearable
        class="pre-order-filter"
        style="max-width: 240px;"
      />
      <v-select
        v-model="statusFilter"
        :items="statusFilterOptions"
        item-value="value"
        item-text="label"
        :label="$t('filterLabel')"
        background-color="primaryVariant2"
        outlined
        dense
        hide-details
        flat
        class="pre-order-filter"
        style="max-width: 180px;"
      />
      <v-spacer></v-spacer>
      <v-btn color="primary" depressed @click="openNewPreOrder()">
        {{ $t('newPreOrder') }}
      </v-btn>
    </v-row>

    <v-row>
      <v-col class="ml-3">
        <v-data-table
          :headers="headers"
          :items="filteredPreOrders"
          :loading="getIsPreOrderLoading"
          :footer-props="{ itemsPerPageOptions: [10, 20, 50], itemsPerPageText: $t('rowsPerPage') }"
          :items-per-page="20"
          class="elevation-1 ma-4"
        >
          <template v-slot:[`footer.page-text`]="items">
            {{ items.pageStart }} - {{ items.pageStop }} {{ $t('of') }} {{ items.itemsLength }}
          </template>

          <template v-slot:[`item.creationDate`]="{ item }">
            {{ formatDate(item.creationDate) }}
          </template>

          <template v-slot:[`item.clientName`]="{ item }">
            {{ item.client ? item.client.name : '—' }}
          </template>

          <template v-slot:[`item.clientCi`]="{ item }">
            {{ item.client && item.client.ci ? item.client.ci : '—' }}
          </template>

          <template v-slot:[`item.engine`]="{ item }">
            {{ formatEngine(item) }}
          </template>

          <template v-slot:[`item.engineSeries`]="{ item }">
            {{ formatEngineSeries(item) }}
          </template>

          <template v-slot:[`item.servicesCount`]="{ item }">
            {{ countServices(item) }}
          </template>

          <template v-slot:[`item.total`]="{ item }">
            {{ formatTotal(item) }}
          </template>

          <template v-slot:[`item.expiresAt`]="{ item }">
            {{ formatExpiresAt(item.creationDate) }}
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip small :color="statusColor(item)" dark>
              {{ statusLabel(item) }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on" @click.stop="printPreOrder(item)">
                  <v-icon small>mdi-printer-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('tooltipPrint') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on" @click.stop="openEditPreOrder(item)">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('tooltipEdit') }}</span>
            </v-tooltip>

            <v-tooltip bottom v-if="!item.convertedAt">
              <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on" @click.stop="askConvert(item)">
                  <v-icon small>mdi-file-move-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('tooltipConvert') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on" @click.stop="askDelete(item)">
                  <v-icon small color="error">mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('tooltipDelete') }}</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <p>{{ $t('noResults') }}</p>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Pre-order create/edit modal -->
    <RectifierPreOrderDetailModal
      ref="preOrderModal"
      @saved="retrieveAllPreOrders()"
    />

    <!-- Order detail modal (for conversion) -->
    <RectifierOrderDetailModal ref="orderDetailModal" />

    <!-- Convert confirmation dialog -->
    <v-dialog v-model="convertDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ $t('convertTitle') }}</v-card-title>
        <v-card-text>{{ $t('convertMessage') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="convertDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" depressed :loading="isConverting" @click="confirmConvert()">
            {{ $t('convertConfirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ $t('deleteTitle') }}</v-card-title>
        <v-card-text>{{ $t('deleteMessage') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" depressed :loading="isDeleting" @click="confirmDelete()">
            {{ $t('deleteConfirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import RectifierPreOrderDetailModal from '@/components/order/rectifier-pre-order-detail-modal/RectifierPreOrderDetailModal.vue';
import RectifierOrderDetailModal from '@/components/order/rectifier-order-detail-modal/RectifierOrderDetailModal.vue';

const PRE_ORDER_VALIDITY_DAYS = 15;

export default {
  name: 'RectifierPreOrders',

  components: {
    RectifierPreOrderDetailModal,
    RectifierOrderDetailModal
  },

  data() {
    return {
      searchClient: '',
      searchEngineSeries: '',
      statusFilter: 'ALL',
      convertDialog: false,
      deleteDialog: false,
      selectedPreOrder: null,
      isConverting: false,
      isDeleting: false
    };
  },

  computed: {
    ...mapGetters('preOrders', ['allPreOrders']),
    ...mapGetters('loading', ['getIsPreOrderLoading']),

    headers() {
      return [
        { text: this.$t('colDate'), value: 'creationDate', sortable: true, class: 'primary--text' },
        { text: this.$t('colClient'), value: 'clientName', sortable: false, class: 'primary--text' },
        { text: this.$t('colCi'), value: 'clientCi', sortable: false, class: 'primary--text' },
        { text: this.$t('colEngine'), value: 'engine', sortable: false, class: 'primary--text' },
        { text: this.$t('colEngineSeries'), value: 'engineSeries', sortable: false, class: 'primary--text' },
        { text: this.$t('colServices'), value: 'servicesCount', sortable: false, align: 'center', class: 'primary--text' },
        { text: this.$t('colTotal'), value: 'total', sortable: false, align: 'right', class: 'primary--text' },
        { text: this.$t('colExpires'), value: 'expiresAt', sortable: false, class: 'primary--text' },
        { text: this.$t('colStatus'), value: 'status', sortable: false, class: 'primary--text' },
        { text: this.$t('colActions'), value: 'actions', sortable: false, align: 'center', class: 'primary--text' }
      ];
    },

    statusFilterOptions() {
      return [
        { value: 'ALL', label: this.$t('filterAll') },
        { value: 'active', label: this.$t('filterActive') },
        { value: 'expired', label: this.$t('filterExpired') },
        { value: 'converted', label: this.$t('filterConverted') }
      ];
    },

    filteredPreOrders() {
      let list = this.allPreOrders;

      if (this.searchClient && this.searchClient.trim()) {
        const q = this.searchClient.trim().toLowerCase();
        list = list.filter(p => {
          const name = (p.client && p.client.name) ? p.client.name.toLowerCase() : '';
          const ci = (p.client && p.client.ci) ? String(p.client.ci) : '';
          return name.includes(q) || ci.includes(q);
        });
      }

      if (this.searchEngineSeries && this.searchEngineSeries.trim()) {
        const q = this.searchEngineSeries.trim().toLowerCase();
        list = list.filter(p => {
          const series = this.getEngineSeries(p).toLowerCase();
          return series.includes(q);
        });
      }

      if (this.statusFilter !== 'ALL') {
        list = list.filter(p => this.getStatus(p) === this.statusFilter);
      }

      return list;
    }
  },

  async created() {
    await this.retrieveAllPreOrders();
  },

  methods: {
    ...mapActions('preOrders', [
      'retrieveAllPreOrders',
      'convertPreOrder',
      'removePreOrder'
    ]),

    printPreOrder(preOrder) {
      this.$root.$emit('print-pre-order-pdf', preOrder.id);
    },

    openNewPreOrder() {
      this.$refs.preOrderModal.open(null);
    },

    openEditPreOrder(preOrder) {
      this.$refs.preOrderModal.open(preOrder);
    },

    askConvert(preOrder) {
      this.selectedPreOrder = preOrder;
      this.convertDialog = true;
    },

    async confirmConvert() {
      if (!this.selectedPreOrder) return;
      this.isConverting = true;
      try {
        await this.convertPreOrder(this.selectedPreOrder.id);
        this.$root.$emit('open-status-message-snackbar', {
          message: this.$t('successConverted'),
          messageType: 'success'
        });
        this.convertDialog = false;
        this.$refs.orderDetailModal.openWithPreOrder(this.selectedPreOrder);
      } catch (error) {
        console.error('Error converting pre-order:', error);
        this.$root.$emit('open-status-message-snackbar', {
          message: this.$t('errorConverting'),
          messageType: 'error'
        });
      } finally {
        this.isConverting = false;
        this.selectedPreOrder = null;
      }
    },

    askDelete(preOrder) {
      this.selectedPreOrder = preOrder;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.selectedPreOrder) return;
      this.isDeleting = true;
      try {
        await this.removePreOrder(this.selectedPreOrder.id);
        this.$root.$emit('open-status-message-snackbar', {
          message: this.$t('successDeleted'),
          messageType: 'success'
        });
        this.deleteDialog = false;
      } catch (error) {
        console.error('Error deleting pre-order:', error);
        this.$root.$emit('open-status-message-snackbar', {
          message: this.$t('errorDeleting'),
          messageType: 'error'
        });
      } finally {
        this.isDeleting = false;
        this.selectedPreOrder = null;
      }
    },

    getStatus(preOrder) {
      if (preOrder.convertedAt) return 'converted';
      const expiresAt = moment(preOrder.creationDate).add(PRE_ORDER_VALIDITY_DAYS, 'days');
      return moment().isAfter(expiresAt) ? 'expired' : 'active';
    },

    statusLabel(preOrder) {
      const s = this.getStatus(preOrder);
      if (s === 'converted') return this.$t('statusConverted');
      if (s === 'expired') return this.$t('statusExpired');
      return this.$t('statusActive');
    },

    statusColor(preOrder) {
      const s = this.getStatus(preOrder);
      if (s === 'converted') return 'blue-grey';
      if (s === 'expired') return 'error';
      return 'success';
    },

    formatDate(date) {
      if (!date) return '—';
      return moment(date).format('DD/MM/YYYY');
    },

    formatExpiresAt(creationDate) {
      if (!creationDate) return '—';
      return moment(creationDate).add(PRE_ORDER_VALIDITY_DAYS, 'days').format('DD/MM/YYYY');
    },

    formatEngine(preOrder) {
      if (!preOrder.detail) return '—';
      const spec = preOrder.detail.item && preOrder.detail.item.specification;
      if (!spec) return '—';
      const parts = [spec.brand, spec.numberOfCylinders]
        .map(v => (v != null ? String(v).trim() : ''))
        .filter(Boolean);
      return parts.length ? parts.join(', ') : '—';
    },

    formatEngineSeries(preOrder) {
      return this.getEngineSeries(preOrder) || '—';
    },

    getEngineSeries(preOrder) {
      if (!preOrder.detail || !preOrder.detail.item) return '';
      const spec = preOrder.detail.item.specification;
      return spec && spec.engine ? String(spec.engine).trim() : '';
    },

    countServices(preOrder) {
      if (!preOrder.detail || !preOrder.detail.services) return 0;
      return preOrder.detail.services.length;
    },

    formatTotal(preOrder) {
      if (!preOrder.detail || !preOrder.detail.services) return '—';
      let total = 0;
      preOrder.detail.services.forEach(s => {
        if (s.subServices && s.subServices.length > 0) {
          s.subServices.forEach(ss => {
            total += Number(ss.price) || 0;
          });
        } else {
          total += Number(s.price) || 0;
        }
      });
      const discount = Number(preOrder.servicesDiscount) || 0;
      return (total - discount).toFixed(2);
    }
  }
};
</script>

<style scoped>
.pre-order-filter {
  min-width: 160px;
}
</style>
